import { useState, useEffect } from 'react';
import { ArrowUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export const PDP_DETAILS_ANCHOR_ID = 'pdp-details';
export const PDP_RECOMMENDATIONS_ANCHOR_ID = 'pdp-recommendations';

export const pdpAnchorScrollMarginClass = 'scroll-mt-[calc(var(--header-height)+3rem)]';

function scrollToAnchor(id: string): void {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
}

/**
 * Mobile-only fixed anchor bar for PDP: Details, Recommendations, scroll to top.
 * Only renders when scrolled past a threshold to avoid being in the layout flow.
 */
export default function PdpStickyAnchorBar({
    className,
    hasRecommendations,
    scrollThreshold = 300,
}: {
    className?: string;
    hasRecommendations: boolean;
    scrollThreshold?: number;
}) {
    const { t } = useTranslation('product');
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsVisible(window.scrollY > scrollThreshold);
        };

        handleScroll();

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [scrollThreshold]);

    if (!isVisible) {
        return null;
    }

    return (
        <nav
            aria-label={t('stickyAnchorBar.navLabel')}
            style={{
                top: 'calc(var(--header-visible-offset, 0px) - 1px)',
            }}
            className={cn(
                'fixed top-0 left-0 right-0 z-40 md:hidden',
                'flex items-center justify-between gap-4',
                'border-b border-sand-90 bg-neutral-white px-6 py-3 shadow-sm',
                'animate-in fade-in slide-in-from-top-2 duration-200',
                className
            )}
            data-testid="pdp-sticky-anchor-bar">
            <div className="flex min-w-0 flex-1 items-center justify-between gap-4">
                <button
                    type="button"
                    className="body-copy-m-bold shrink-0 underline underline-offset-2 decoration-solid text-neutral-10"
                    onClick={() => scrollToAnchor(PDP_DETAILS_ANCHOR_ID)}>
                    {t('stickyAnchorBar.details')}
                </button>
                {hasRecommendations && (
                    <button
                        type="button"
                        className="body-copy-m-bold shrink-0 underline underline-offset-2 decoration-solid text-neutral-10"
                        onClick={() => scrollToAnchor(PDP_RECOMMENDATIONS_ANCHOR_ID)}>
                        {t('stickyAnchorBar.recommendations')}
                    </button>
                )}
            </div>
            <button
                type="button"
                className="inline-flex size-6 shrink-0 items-center justify-center text-neutral-10"
                aria-label={t('stickyAnchorBar.scrollToTop')}
                onClick={scrollToTop}
                data-testid="pdp-sticky-anchor-scroll-top">
                <ArrowUp className="size-5" aria-hidden />
            </button>
        </nav>
    );
}
